import React, { useEffect, useState } from 'react';
import { ArrowSquareOut } from '@phosphor-icons/react';

import { Spinner } from '../ui/spinner';
import { useConvosContext } from './convos';
import { Button } from '../ui/button';

type ContextSufficiency = 'Sufficient' | 'Partially Sufficient' | 'Insufficient';
type AnswerGenerationType = 'Direct Extraction' | 'Summarization' | 'Inference' | 'Combination' | 'Cannot Answer';
type QueryContextRelevance = 'High' | 'Medium' | 'Low';
interface EvidenceFromContext {
  supporting_quote?: string;
  url?: string;
}
export type AnswerDiagnostics = {
  confidence_score: string;
  answer_generation_type?: AnswerGenerationType;
  query_context_relevance?: QueryContextRelevance;
  context_sufficiency?: ContextSufficiency;
  confidence_justification?: string;
  evidence_from_context?: EvidenceFromContext[];
};

interface AnswerDiagnosticsProps {
  messageId: string;
}

export function AnswerDiagnosticsSection({ messageId }: AnswerDiagnosticsProps) {
  const { fetchAnswerDiagnostics } = useConvosContext();
  const [answerDiagnostics, setAnswerDiagnostics] = useState<AnswerDiagnostics | null>(null);
  const [isLoading, setIsLoading] = useState(true); // start loading when it mounts. It will start to fetch answer diagnostics on mount

  useEffect(() => {
    void fetchAnswerDiagnostics(messageId)
      .then((diagnostics) => setAnswerDiagnostics(diagnostics))
      .finally(() => setIsLoading(false));
  }, [fetchAnswerDiagnostics, messageId]);

  return (
    <div className="flex flex-col gap-4 px-4 flex-shrink-0 bg-surface" data-name="discard-dialog-answer-diagnostic">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : (
        <>
          {answerDiagnostics && (
            <>
              <dl className="grid grid-cols-[1fr_2fr] grid-rows-2 gap-5">
                <div className="flex flex-col gap-1">
                  <dt className="heading-xs">Confidence</dt>
                  <dd className="text-sm">{answerDiagnostics.confidence_score}</dd>
                </div>
                {answerDiagnostics.answer_generation_type && (
                  <div className="flex flex-col gap-1">
                    <dt className="heading-xs">Answer Type</dt>
                    <dd className="text-sm">{answerDiagnostics.answer_generation_type}</dd>
                  </div>
                )}
                {answerDiagnostics.query_context_relevance && (
                  <div className="flex flex-col gap-1">
                    <dt className="heading-xs">Relevance</dt>
                    <dd className="text-sm">{answerDiagnostics.query_context_relevance}</dd>
                  </div>
                )}
                {answerDiagnostics.context_sufficiency && (
                  <div className="flex flex-col gap-1">
                    <dt className="heading-xs">Context</dt>
                    <dd className="text-sm">{answerDiagnostics.context_sufficiency}</dd>
                  </div>
                )}
              </dl>
              {answerDiagnostics.confidence_justification && (
                <div className="flex flex-col gap-2" data-name="diagnostic-justification">
                  <span className="heading-xs">Justification</span>
                  <p className="text-sm my-0">{answerDiagnostics.confidence_justification}</p>
                </div>
              )}
              {answerDiagnostics.evidence_from_context && answerDiagnostics.evidence_from_context.length > 0 && (
                <div className="flex flex-col gap-2" data-name="diagnostic-evidence">
                  <span className="heading-xs">Evidence (from Knowledge Base)</span>
                  {answerDiagnostics.evidence_from_context.map((evidence, index) => (
                    <div key={index} className="flex flex-col gap-1">
                      {evidence.supporting_quote && <p className="text-sm my-0">{evidence.supporting_quote}</p>}
                      {evidence.url && (
                        <Button variant="link" size="sm" className="flex gap-1 text-sm self-end items-center" asChild>
                          <a href={evidence.url} target="_blank" rel="noopener noreferrer">
                            <span>Source</span>
                            <ArrowSquareOut size={16} />
                          </a>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
